/**
 * Replay log — everything from outside the simulation that changed the course of a match, in
 * order, at the exact point it was applied. Together with the config it reproduces the match
 * bit for bit (the engine is deterministic: seeded Rng, plain-data state).
 *
 * What is recorded is the *applied* command stream — human AND AI commands — not merely the
 * human input. Reasons:
 *  - AI controllers keep private memory between thinks; it cannot be checkpointed, so a replay
 *    that re-ran the AIs could not seek. Recording their commands lets a replay skip them.
 *  - The replay stays exact when AI code changes between recording and replaying (a server and
 *    a client of different builds, or an AI tuning pass) — only the rules engine must match.
 *  - Re-simulating without AI thinking is several times faster, which makes scrubbing cheap.
 *
 * Application points (see GameSession.step / flushCommands):
 *  - `step`  — commands queued before step() and applied at its start. `t` is the tick the step
 *              produced (state.tick after the increment).
 *  - `flush` — commands applied between ticks by flushCommands(); `t` is the current tick.
 *  - `ctrl`  — a setController() that changed a faction's controller (AI takeover / restore).
 * Plain data only (structured-clone and msgpack friendly).
 */
import type { Command } from '../commands';
import type { Controller, FactionId, SessionConfig, Tick } from '../core/types';

/** Bumped whenever the entry format or its semantics change; readers refuse other versions. */
export const REPLAY_VERSION = 1;

/** One applied command: `[faction, command]` (a tuple keeps the log compact on the wire). */
export type RecordedCommand = [FactionId, Command];

export type ReplayEntry =
  | { k: 'step'; t: Tick; c: RecordedCommand[] }
  | { k: 'flush'; t: Tick; c: RecordedCommand[] }
  | { k: 'ctrl'; t: Tick; f: FactionId; c: Controller };

export interface ReplayLog {
  version: number;
  config: SessionConfig;
  entries: ReplayEntry[];
  /** Tick the recording ends at (the final tick when the match is over). */
  endTick: Tick;
  /** `hashState` at `endTick`, after every entry — lets a replayer prove it reproduced the match. */
  endHash: string;
}

/** Appends entries as a live GameSession applies commands (one per session). */
export class ReplayRecorder {
  private readonly entries: ReplayEntry[] = [];

  commands(k: 'step' | 'flush', t: Tick, batch: readonly { faction: FactionId; command: Command }[]): void {
    if (batch.length === 0) return;
    // The queue already holds private copies (GameSession.submit copies), never mutated later.
    this.entries.push({ k, t, c: batch.map(({ faction, command }): RecordedCommand => [faction, command]) });
  }

  controller(t: Tick, f: FactionId, c: Controller): void {
    this.entries.push({ k: 'ctrl', t, f, c });
  }

  log(config: SessionConfig, endTick: Tick, endHash: string): ReplayLog {
    return { version: REPLAY_VERSION, config, entries: this.entries.slice(), endTick, endHash };
  }
}

/** Throws with a readable message unless `log` looks like a ReplayLog this engine can play. */
export function checkReplayLog(log: ReplayLog): void {
  if (!log || typeof log !== 'object') throw new Error('not a replay log');
  if (log.version !== REPLAY_VERSION)
    throw new Error(
      `replay log version ${String(log.version)} is not supported (expected ${REPLAY_VERSION})`,
    );
  if (!log.config || !Array.isArray(log.entries) || !Number.isInteger(log.endTick) || log.endTick < 0)
    throw new Error('malformed replay log');
  let prev = 0;
  for (const e of log.entries) {
    if (!e || !Number.isInteger(e.t) || e.t < prev || e.t > log.endTick)
      throw new Error('replay entries out of order');
    // A step entry is applied inside the step that produces tick t: it comes before every other
    // entry of tick t, and t ≥ 1.
    if (
      e.k === 'step'
        ? e.t <= prev || !Array.isArray(e.c)
        : e.k === 'flush'
          ? !Array.isArray(e.c)
          : e.k !== 'ctrl'
    )
      throw new Error('malformed replay entry');
    prev = e.t;
  }
}
